import { useState } from 'react'
import type { OnboardingCredential } from './onboardClient.js'

/** The logins as plain text, one block each, for pasting into a message or notes before a demo. */
export function credentialsAsText(credentials: readonly OnboardingCredential[], signInUrl: string): string {
  const blocks = credentials.map(
    (credential) => `${credential.role} - ${credential.name} (${credential.access})\n  Username: ${credential.username}\n  Password: ${credential.password}`,
  )
  return [`Sign in at ${signInUrl}`, ...blocks].join('\n\n')
}

function CopyTextButton({ value, label, copiedLabel = 'Copied' }: { value: string; label: string; copiedLabel?: string }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard unavailable - the values are still on screen and selectable.
    }
  }

  return (
    <button type="button" onClick={() => void handleCopy()} className="text-[11px] font-bold text-marine hover:text-marine-dk whitespace-nowrap">
      {copied ? copiedLabel : label}
    </button>
  )
}

/**
 * Every login onboarding created, with its one-time password. This is the
 * only moment the passwords exist in readable form - the API keeps hashes -
 * so each value can be copied on its own, and the whole set in one go.
 * Onboarding is the one place logins are still made with a password rather
 * than invited (invitations plan D8): a demo's people are not real, and the
 * first operator of a real client is handed over on this screen.
 */
export function OnboardingCredentials({ credentials }: { credentials: readonly OnboardingCredential[] }) {
  // The dashboard is this same site, at its root.
  const signInUrl = window.location.origin

  return (
    <div className="border border-marine/15 p-4 mb-6">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="text-xs font-bold text-ink">{credentials.length === 1 ? 'Operator dashboard login' : 'Dashboard logins'}</div>
        {credentials.length > 1 && <CopyTextButton value={credentialsAsText(credentials, signInUrl)} label="Copy all" copiedLabel="Copied all" />}
      </div>
      <p className="text-xs text-ink-mid mb-3">
        These logins were made directly, with a password, rather than by invitation - a demo's people are not real, and a new client's first operator is handed over here.
        Every later login is invited and sets its own password. Passwords are shown once and are not stored: if one is lost, the person can use Forgot your password at sign-in,
        or you can rotate it or issue a reset link from the client's panel. Everyone signs in at <span className="font-mono">{signInUrl}</span>.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
              <th className="py-2 pr-3 font-bold">Login</th>
              <th className="py-2 pr-3 font-bold">Access</th>
              <th className="py-2 pr-3 font-bold">Username</th>
              <th className="py-2 pr-3 font-bold">Password</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((credential) => (
              <tr key={credential.username} className="border-b border-marine/10 align-top">
                <td className="py-2 pr-3">
                  <div className="font-bold text-ink">{credential.name}</div>
                  <div className="text-[11px] text-ink-mid">{credential.role}</div>
                </td>
                <td className="py-2 pr-3 text-ink-mid">{credential.access}</td>
                <td className="py-2 pr-3">
                  <div className="font-mono text-ink select-all break-all">{credential.username}</div>
                  <CopyTextButton value={credential.username} label="Copy username" />
                </td>
                <td className="py-2 pr-3">
                  <div className="font-mono text-ink select-all break-all">{credential.password}</div>
                  <CopyTextButton value={credential.password} label="Copy password" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
